import { inject, Injectable } from '@angular/core';
import { CompressionService } from './compression.service';

@Injectable({
	providedIn: 'root',
})
export class CompressedStorageService {
	private compressionService = inject(CompressionService);

	constructor() {}

	/**
	 * Gets the number of items in localStorage
	 * @returns number The number of items
	 */
	get length(): number {
		return localStorage.length;
	}

	/**
	 * Sets an item in localStorage with automatic compression for large data
	 * @param key The storage key
	 * @param value The value to store (will be JSON stringified)
	 */
	setItem(key: string, value: any) {
		try {
			const jsonString = JSON.stringify(value);

			// Only compress if data is large enough and compression is supported
			if (this.compressionService.shouldCompress(jsonString)) {
				const compressedData = this.compressionService.compressSync(jsonString);
				localStorage.setItem(key, compressedData);
			} else {
				localStorage.setItem(key, jsonString);
			}
		} catch (error) {
			console.error('Error setting compressed localStorage item:', error);
			// Fallback to uncompressed storage
			try {
				localStorage.setItem(key, JSON.stringify(value));
			} catch (fallbackError) {
				console.error('Fallback storage also failed:', fallbackError);
				throw fallbackError;
			}
		}
	}

	/**
	 * Gets an item from localStorage with automatic decompression
	 * @param key The storage key
	 * @returns Promise<any> The stored value (parsed from JSON)
	 */
	getItem<T>(key: string): T | null {
		try {
			const storedValue = localStorage.getItem(key);

			if (storedValue === null) {
				return null;
			}

			// Check if the data is compressed
			if (storedValue.startsWith('compressed:')) {
				const decompressedData = this.compressionService.decompressSync(storedValue);
				return JSON.parse(decompressedData);
			} else {
				// Not compressed, parse directly
				return JSON.parse(storedValue);
			}
		} catch (error) {
			console.error('Error getting compressed localStorage item:', error);
			// Fallback to direct localStorage access
			try {
				const fallbackValue = localStorage.getItem(key);
				return fallbackValue ? JSON.parse(fallbackValue) : null;
			} catch (fallbackError) {
				console.error('Fallback retrieval also failed:', fallbackError);
				return null;
			}
		}
	}

	/**
	 * Removes an item from localStorage
	 * @param key The storage key
	 */
	removeItem(key: string): void {
		localStorage.removeItem(key);
	}

	/**
	 * Clears all items from localStorage
	 */
	clear(): void {
		localStorage.clear();
	}

	/**
	 * Gets a key by index
	 * @param index The index
	 * @returns string The key at the specified index
	 */
	key(index: number): string | null {
		return localStorage.key(index);
	}
}
